import type { PropertyFilterProperty } from '@cloudscape-design/collection-hooks';

import type { FieldFilterConfig, TableRecord } from '../types';
import { yesNoCell } from './yesNoCell';

/**
 * Filter options for a Yes/No (nullable boolean) column.
 *
 * The label is what the user picks; the value stays `'true'`/`'false'` so a token
 * produced by the fixed UI is byte-identical to one saved via the old
 * true/false workaround, and to the tokens the universal-widget dashboard
 * clickthrough emits (`String(item.SomeBool)`).
 */
export const YES_NO_FILTER_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;

/**
 * Normalise a filter token value to the boolean it means.
 *
 * Accepts the `'Yes'`/`'No'` labels as well as the `'true'`/`'false'` values, so
 * filters saved with the old workaround keep resolving and free-text entry of
 * "Yes"/"No" works too. Returns `undefined` for anything that isn't a Yes/No
 * value — callers treat that as "matches nothing" rather than guessing.
 */
export const parseYesNoFilterValue = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  switch (value.trim().toLowerCase()) {
    case 'true':
    case 'yes':
      return true;
    case 'false':
    case 'no':
      return false;
    default:
      return undefined;
  }
};

/**
 * Token values that mean "the field is blank".
 *
 * The universal-widget dashboard builds its blank clickthrough token as
 * `String(item.SomeBool)` → `'null'` (see `data-sources/issue.ts`
 * `categoryGetter`), and CDS offers an explicit `{ value: 'null', label: 'Blank' }`
 * option — so `'null'` is the platform-wide sentinel. `'-'` is
 * `defaultClickthroughFilterWithUnrated`'s `unratedValue` default.
 *
 * An empty string is deliberately NOT a blank sentinel: an empty token is far
 * more likely half-typed input than a deliberate "show me the blanks".
 */
const BLANK_TOKEN_VALUES = ['null', 'undefined', '-'];

/** True when the filter token asks for blank (unset) rows. */
export const isYesNoBlankToken = (value: unknown): boolean =>
  typeof value === 'string' &&
  BLANK_TOKEN_VALUES.includes(value.trim().toLowerCase());

const isBlankRowValue = (itemValue: unknown): boolean =>
  itemValue === null || itemValue === undefined;

/** Label shown on the filter token pill for a stored `'true'`/`'false'` value. */
export const formatYesNoFilterValue = (value: unknown): string => {
  // Mirrors the dashboard's own segment label, so a clickthrough from the
  // "Unspecified" slice produces a pill reading "Unspecified" rather than a
  // blank one.
  if (isYesNoBlankToken(value)) {
    return 'Unspecified';
  }

  const parsed = parseYesNoFilterValue(value);
  if (parsed === undefined) {
    return '';
  }

  return parsed ? 'Yes' : 'No';
};

/**
 * `=` match. A blank (null/undefined) field matches neither Yes nor No, so blank
 * records stay out of both result sets.
 */
export const matchesYesNoFilterValue = (
  itemValue: unknown,
  tokenValue: unknown
): boolean => {
  // `= Unspecified` selects exactly the blank rows. This is how the dashboard's
  // "Unspecified" pie segment clicks through to the register.
  if (isYesNoBlankToken(tokenValue)) {
    return isBlankRowValue(itemValue);
  }

  const expected = parseYesNoFilterValue(tokenValue);
  if (expected === undefined || isBlankRowValue(itemValue)) {
    return false;
  }

  return itemValue === expected;
};

/**
 * `!=` match. Blanks DO appear under `!= Yes` — that is the literal reading of
 * "not Yes" and preserves the pre-fix behaviour; only the `=` case is
 * constrained to exclude blanks.
 *
 * Deliberately not `!matchesYesNoFilterValue(...)`: an unparseable token would
 * then match *every* row, contradicting the "matches nothing" contract of
 * `parseYesNoFilterValue` and reading to the user as a filter that did nothing.
 */
export const doesNotMatchYesNoFilterValue = (
  itemValue: unknown,
  tokenValue: unknown
): boolean => {
  // `!= Unspecified` is the complement: every row that HAS a value.
  if (isYesNoBlankToken(tokenValue)) {
    return !isBlankRowValue(itemValue);
  }

  const expected = parseYesNoFilterValue(tokenValue);
  if (expected === undefined) {
    return false;
  }

  return itemValue !== expected;
};

const yesNoOperators: Partial<PropertyFilterProperty>['operators'] = [
  {
    operator: '=',
    format: formatYesNoFilterValue,
    match: matchesYesNoFilterValue,
  },
  {
    operator: '!=',
    format: formatYesNoFilterValue,
    match: doesNotMatchYesNoFilterValue,
  },
];

/**
 * Filter config for a Yes/No column: offers "Yes"/"No" in the dropdown and
 * matches them against the underlying boolean.
 */
export const yesNoFilterConfig = <
  T extends TableRecord,
>(): FieldFilterConfig<T> => ({
  filteringOptions: YES_NO_FILTER_OPTIONS.map((option) => ({ ...option })),
  filteringProperties: { operators: yesNoOperators },
});

/**
 * Cell renderer + filter config for a Yes/No column, so the two are declared
 * together and can't drift apart. Spread it into the column config:
 *
 * ```ts
 * ImpactsCustomer: { custom: false, formId, fieldId, ...yesNoColumn('ImpactsCustomer') },
 * ```
 */
export const yesNoColumn = <T extends TableRecord>(key: keyof T) => ({
  cell: yesNoCell<T>(key),
  filterOptions: yesNoFilterConfig<T>(),
});
